/**
 * React Hooks for Schedule Data
 */

import { useCallback, useEffect, useState } from 'react';
import { scheduleRepository } from '../repositories/ScheduleRepository';
import {
    EntityFilterOptions,
    EntitySortOptions,
    Schedule
} from '../types/WorkflowEntities';

// Hook for fetching all schedules
export function useSchedules(options?: {
    filter?: EntityFilterOptions;
    sort?: EntitySortOptions;
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await scheduleRepository.findAll({
                filter: options?.filter as any,
                sort: options?.sort as any
            });
            setSchedules(data);
        } catch (err) {
            setError(err as Error);
            setSchedules([]);
        } finally {
            setLoading(false);
        }
    }, [options?.filter, options?.sort]);

    useEffect(() => {
        fetchSchedules();

        if (options?.autoRefresh && options?.refreshInterval) {
            const interval = setInterval(fetchSchedules, options.refreshInterval);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [fetchSchedules, options?.autoRefresh, options?.refreshInterval]);

    return {
        schedules,
        loading,
        error,
        refetch: fetchSchedules
    };
}

// Hook for fetching a single schedule
export function useSchedule(scheduleId: string | null) {
    const [schedule, setSchedule] = useState<Schedule | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchSchedule = useCallback(async () => {
        if (!scheduleId) {
            setSchedule(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await scheduleRepository.findById(scheduleId);
            setSchedule(data);
        } catch (err) {
            setError(err as Error);
            setSchedule(null);
        } finally {
            setLoading(false);
        }
    }, [scheduleId]);

    useEffect(() => {
        fetchSchedule();
    }, [fetchSchedule]);

    return {
        schedule,
        loading,
        error,
        refetch: fetchSchedule
    };
}

// Hook for schedule CRUD operations
export function useScheduleMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createSchedule = useCallback(async (data: Partial<Schedule>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.create(data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateSchedule = useCallback(async (id: string, data: Partial<Schedule>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.update(id, data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteSchedule = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.delete(id);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const enableSchedule = useCallback(async (scheduleId: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.enable(scheduleId);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const disableSchedule = useCallback(async (scheduleId: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.disable(scheduleId);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const triggerSchedule = useCallback(async (scheduleId: string, payload?: any) => {
        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.trigger(scheduleId, payload);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createSchedule,
        updateSchedule,
        deleteSchedule,
        enableSchedule,
        disableSchedule,
        triggerSchedule,
        loading,
        error
    };
}

// Hook for schedule search
export function useScheduleSearch(query: string, debounceMs: number = 300) {
    const [results, setResults] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cleanup: (() => void) | undefined;
        if (!query || query.trim() === '') {
            setResults([]);
        } else {
            const timer = setTimeout(async () => {
                setLoading(true);
                setError(null);

                try {
                    const data = await scheduleRepository.search(query);
                    setResults(data);
                } catch (err) {
                    setError(err as Error);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, debounceMs);
            cleanup = () => clearTimeout(timer);
        }
        return cleanup;
    }, [query, debounceMs]);

    return {
        results,
        loading,
        error
    };
}

// Hook for upcoming schedules
export function useUpcomingSchedules(limit?: number) {
    const [schedules, setSchedules] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchUpcoming = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await scheduleRepository.getUpcoming(limit);
                setSchedules(data);
            } catch (err) {
                setError(err as Error);
                setSchedules([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUpcoming();
    }, [limit]);

    return {
        schedules,
        loading,
        error
    };
}

// Hook for schedule history
export function useScheduleHistory(scheduleId: string | null, limit?: number) {
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!scheduleId) {
            setHistory([]);
            return;
        }

        const fetchHistory = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await scheduleRepository.getHistory(scheduleId, limit);
                setHistory(data);
            } catch (err) {
                setError(err as Error);
                setHistory([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [scheduleId, limit]);

    return {
        history,
        loading,
        error
    };
}

// Hook for validating cron expressions
export function useCronValidator() {
    const [validation, setValidation] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const validateCron = useCallback(async (cronExpression: string) => {
        if (!cronExpression || cronExpression.trim() === '') {
            setValidation(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await scheduleRepository.validateCron(cronExpression);
            setValidation(result);
        } catch (err) {
            setError(err as Error);
            setValidation(null);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        validation,
        validateCron,
        loading,
        error
    };
}

export default {
    useSchedules,
    useSchedule,
    useScheduleMutations,
    useScheduleSearch,
    useUpcomingSchedules,
    useScheduleHistory,
    useCronValidator
};